// src/components/pedigree/CanRiskModal.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import {
  Modal,
  Box,
  Typography,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ThemeContext, SnackContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import CanRiskCancerEvidence from './CanRiskCancerEvidence';

// ------------------------------
// Static look-up tables
// ------------------------------
const receptors = ['ER', 'PR', 'HER2', 'CK14', 'CK56'];

const cancerSites = [
  { key: 'BC1', label: 'First breast cancer' },
  { key: 'BC2', label: 'Second breast cancer' },
  { key: 'OC', label: 'Ovarian cancer' },
  { key: 'PRO', label: 'Prostate cancer' },
  { key: 'PAN', label: 'Pancreatic cancer' }
];

// Genes covered by the CanRisk specification (v4 — the serializer writes the
// 28-column CanRisk 4.0 header, which includes HOXB13).
const genes = [
  'BRCA1',
  'BRCA2',
  'PALB2',
  'ATM',
  'CHEK2',
  'BARD1',
  'RAD51D',
  'RAD51C',
  'BRIP1',
  'HOXB13'
];

export default function CanRiskModal({
  open,
  onClose,
  onDownload,
  userId,
  data
}) {
  const t = useAutoTranslation();

  const { theme } = useContext(ThemeContext);
  const { setSnack } = useContext(SnackContext);

  // pages holds an array of member options
  const [pages, setPages] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [jumpUPN, setJumpUPN] = useState('');
  const [canRiskConfigured, setCanRiskConfigured] = useState(false);
  const [canRiskChecking, setCanRiskChecking] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [scoreResult, setScoreResult] = useState(null);
  const [scoreError, setScoreError] = useState('');

  // TGCLOUD-1575. ONE resolution of "who is this calculation for", derived from
  // the selected node. There used to be two: this one, and a separate
  // resolveTargetId() further down that also consulted data.proBandId. They
  // disagreed whenever a node carried proBandId but no familyMemberId, so the
  // modal could fetch one person's risk-factor pages and submit a calculation
  // for another. Any change here must serve BOTH the fillup query and the
  // download/calculate bodies.
  const selectedTargetId =
    data?.data?.familyMemberId ||
    data?.data?.proBandId ||
    data?.familyMemberId ||
    data?.proBandId ||
    null;

  // Family view opens with no node selected; the wizard page is then the
  // selection, but `pages` does not exist yet at query time, so the fetch falls
  // back to the root's key. The page-derived fallback is applied at submit time
  // (resolveTargetId below).
  const targetId = selectedTargetId || 'proband';

  // React Query (the convention used elsewhere in this feature — see
  // PedigreeChartView / FamilyTable) replaces a module-level Map that survived
  // unmount and re-login and had no invalidation at all: its `fetchedAt` was
  // written but never read, so a stale page set could outlive a pedigree edit for
  // the whole page session. It also gives us real isLoading / isError, which is
  // what distinguishes "still loading" from "this pedigree has no data" below.
  const {
    data: fillData,
    isLoading: pagesLoading,
    isError: pagesError
  } = useQuery({
    queryKey: ['canrisk-fillup', userId, targetId],
    enabled: Boolean(open && userId),
    queryFn: async () => {
      const { data: resp } = await axios.get(
        `${APICONSTANTS.fillCanRiskModal.replace(
          ':user_id',
          userId
        )}?targetId=${targetId}`
      );
      return resp;
    }
  });

  // Seed the editable copy once the fetch lands. `pages` stays local state
  // because the modal edits it in place before saving.
  useEffect(() => {
    if (!fillData) return;
    setPages(fillData.pages || []);
    setPageIdx(fillData.targetIndex ?? 0);
  }, [fillData]);

  useEffect(() => {
    if (!open) return;
    setCanRiskChecking(true);
    setCanRiskConfigured(false);
    axios
      .get(`${APICONSTANTS.canRiskSettings}?validate=true`)
      .then((res) => {
        setCanRiskConfigured(
          Boolean(res.data?.configured && res.data?.authenticated)
        );
      })
      .catch(() => setCanRiskConfigured(false))
      .finally(() => setCanRiskChecking(false));
  }, [open]);

  useEffect(() => {
    if (open) return;
    setScoreResult(null);
    setScoreError('');
    setCalculating(false);
  }, [open]);

  // guard against no data
  if (!open) return null;
  if (!Array.isArray(pages) || pages.length === 0) {
    // Three DISTINCT states. Previously all three rendered "Loading CanRisk
    // options…", so a pedigree with no saved data (the fill endpoint answers
    // 200 with an empty list) showed a spinner message for ever.
    let message = t('Loading CanRisk options…');
    if (pagesError) {
      message = t(
        'Could not load CanRisk options. Please close this window and try again.'
      );
    } else if (!pagesLoading) {
      message = t(
        'No CanRisk data is available for this pedigree yet. Save the pedigree, then reopen this window.'
      );
    }
    return (
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            p: 4,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography data-testid="canrisk-modal-status">{message}</Typography>
        </Box>
      </Modal>
    );
  }

  const cur = pages[pageIdx] || {};

  // update current member's settings
  const updateCurrent = (patch) =>
    setPages(pages.map((p, i) => (i === pageIdx ? { ...p, ...patch } : p)));

  const individualDataRows = (() => {
    const d = cur.individualData || {};
    const rows = [];
    const numLabels = {
      menarche: 'Menarche (age)',
      parity: 'Parity',
      ageFirstLiveBirth: 'Age at first live birth',
      bmi: 'BMI',
      alcoholGramsPerDay: 'Daily alcohol (g/day)',
      birads: 'BI-RADS',
      height: 'Height (cm)',
      weight: 'Weight (kg)',
      mammographicDensityVolpara: 'Density %Volpara',
      mammographicDensityVAS: 'Density %VAS'
    };
    Object.entries(numLabels).forEach(([k, label]) => {
      const f = d[k];
      if (f && !f.unknown && f.value !== '' && f.value != null) {
        rows.push([label, f.value]);
      }
    });
    const auxLabels = {
      ocUse: ['OC use', 'years'],
      mhtUse: ['MHT use', 'years'],
      menopause: ['Menopause status', 'age'],
      breastFeeding: ['Breastfeeding', 'months'],
      biopsy: ['Biopsy history', 'count']
    };
    Object.entries(auxLabels).forEach(([k, [label, auxKey]]) => {
      const f = d[k];
      if (f && f.status && f.status !== 'Unknown') {
        const aux = f[auxKey];
        rows.push([label, aux ? `${f.status} (${aux})` : f.status]);
      }
    });
    const yesNoLabels = {
      tubalLigation: 'Tubal ligation',
      endometriosis: 'Endometriosis',
      smoker: 'Smoker'
    };
    Object.entries(yesNoLabels).forEach(([k, label]) => {
      if (d[k] && d[k] !== 'Unknown') {
        rows.push([label, d[k]]);
      }
    });
    const prsLabels = {
      prsBreast: 'PRS — Breast',
      prsOvarian: 'PRS — Ovarian',
      prsProstate: 'PRS — Prostate'
    };
    Object.entries(prsLabels).forEach(([k, label]) => {
      const f = d[k];
      if (f && !f.unknown && (f.alpha || f.zscore)) {
        rows.push([label, `α=${f.alpha ?? '—'}, z=${f.zscore ?? '—'}`]);
      }
    });
    return rows;
  })();

  // ---------------------------------------
  //      Helper renderers
  // ---------------------------------------
  // ------------------------------
  // Receptor table renderer
  // ------------------------------
  const recOpts = ['0', 'N', 'P']; // 0-Unknown, N-Negative, P-Positive

  const renderReceptorTable = () => (
    <Table size="small" sx={{ mt: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell>{t('Receptor')}</TableCell>
          {['Unknown', 'Negative', 'Positive'].map((t) => (
            <TableCell key={t} align="center">
              {t}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      <TableBody>
        {receptors.map((r) => {
          const val = cur.receptorStatus?.[r] ?? '0';
          const setVal = (v) =>
            updateCurrent({
              receptorStatus: { ...cur.receptorStatus, [r]: v }
            });

          return (
            <TableRow key={r}>
              <TableCell>{r}</TableCell>
              {recOpts.map((v) => (
                <TableCell key={v} align="center">
                  <Radio
                    size="small"
                    checked={val === v}
                    onChange={() => setVal(v)}
                  />
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
  // genotype selector (test type + result)
  // ------------------------------
  // Genotype table renderer
  // ------------------------------
  const typeOpts = ['0', 'S', 'T']; // 0-Unknown, S-Mutation, T-Direct
  const resultOpts = ['0', 'P', 'N']; // 0-Unknown, P-Positive, N-Negative

  const renderGenotypeTable = () => (
    <Table size="small">
      {/* 表头 */}
      <TableHead>
        <TableRow>
          <TableCell rowSpan={2}>{t('Gene Test')}</TableCell>
          <TableCell align="center" colSpan={3}>
            {t('Type')}
          </TableCell>
          <TableCell align="center" colSpan={3}>
            {t('Result')}
          </TableCell>
        </TableRow>
        <TableRow>
          {[
            'Unknown',
            'Mutation Search',
            'Direct Gene',
            'Unknown',
            'Positive',
            'Negative'
          ].map((txt) => (
            <TableCell key={txt} align="center">
              {txt}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>

      {/* 表体 */}
      <TableBody>
        {genes.map((gene) => {
          const g = cur.genotype?.[gene] ?? { type: '0', result: '0' };

          const setType = (val) =>
            updateCurrent({
              genotype: { ...cur.genotype, [gene]: { ...g, type: val } }
            });
          const setResult = (val) =>
            updateCurrent({
              genotype: { ...cur.genotype, [gene]: { ...g, result: val } }
            });

          return (
            <TableRow key={gene}>
              <TableCell>
                {gene}&nbsp;{t('Test')}
              </TableCell>

              {/* type radios */}
              {typeOpts.map((v) => (
                <TableCell key={v} align="center">
                  <Radio
                    size="small"
                    checked={g.type === v}
                    onChange={() => setType(v)}
                  />
                </TableCell>
              ))}

              {/* result radios */}
              {resultOpts.map((v) => (
                <TableCell key={v} align="center">
                  <Radio
                    size="small"
                    checked={g.result === v}
                    onChange={() => setResult(v)}
                  />
                </TableCell>
              ))}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );

  // Same selection as the query above, plus the family-view fallback: with no
  // node selected the wizard page IS the selection.
  const resolveTargetId = () => selectedTargetId || cur.familyMemberId;

  const getTargetMember = () => {
    const id = resolveTargetId();
    return (
      pages.find((member) => String(member.familyMemberId) === String(id)) ||
      cur
    );
  };

  // The wizard header names pages[pageIdx], which is whichever person you are
  // BROWSING — not necessarily the one being scored. With a node selected you
  // can page to a relative, edit their risk factors and press Get Score, and
  // the score is still computed for the selected node. Name the target
  // explicitly and permanently so the two can never be confused.
  const targetMemberDisplay = getTargetMember()?.display || '';

  const handleDownload = () => {
    const targetId = resolveTargetId();
    onDownload?.(pages, targetId);
    onClose();
    setSnack({
      status: true,
      msg: t('Downloading…'),
      severity: 'info'
    });
  };

  const handleCalculate = () => {
    const targetMember = getTargetMember();
    if (!targetMember?.hasDob) {
      setSnack({
        status: true,
        msg: t(
          'Enter a date of birth for the target person to run risk calculation.'
        ),
        severity: 'error'
      });
      return;
    }

    const targetId = resolveTargetId();
    if (!userId) {
      setSnack({
        status: true,
        msg: t('Please save the pedigree first'),
        severity: 'error'
      });
      return;
    }

    setScoreResult(null);
    setScoreError('');
    setCalculating(true);
    setSnack({
      status: true,
      msg: t('Calculating…'),
      severity: 'info'
    });

    axios
      .post(APICONSTANTS.calculateCanRiskScore.replace(':user_id', userId), {
        members: pages,
        targetId
      })
      .then((res) => {
        setScoreError('');
        setScoreResult(res.data);
      })
      .catch((err) => {
        // The server refuses (422 CANRISK_TARGET_INVALID) when the pedigree
        // would not identify exactly one person, or when the target is so
        // disconnected that the family cannot be included. Its message is
        // actionable guidance, so surface it verbatim rather than collapsing
        // it into the generic catch-all below.
        const errorMessage =
          (err?.response?.data?.code === 'CANRISK_TARGET_INVALID' &&
            err?.response?.data?.message) ||
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.response?.data?.['Model Error'] ||
          err?.response?.data?.['Pedigree Error'] ||
          t('Error calculating CanRisk scores');
        setScoreResult(null);
        setScoreError(errorMessage);
      })
      .finally(() => {
        setCalculating(false);
      });
  };

  const pedigreeResult = scoreResult?.pedigree_result?.[0];
  const tenYearRisk = pedigreeResult?.ten_yr_cancer_risk?.[0];
  const lifetimeRisk = pedigreeResult?.lifetime_cancer_risk?.[0];
  const mutationProbabilities = Array.isArray(
    pedigreeResult?.mutation_probabilties
  )
    ? pedigreeResult.mutation_probabilties
    : [];

  // ---------------------------------------
  //          Render modal
  // ---------------------------------------

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            maxHeight: 700,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            boxShadow: 24,

            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ color: '#fff' }}>
                {t('CanRisk –')} {cur.display || t('Unknown')}
              </Typography>
              <Typography
                variant="caption"
                data-testid="canrisk-target-label"
                sx={{ color: '#fff', opacity: 0.85 }}
              >
                {t('Risk is calculated for: {{value}}', {
                  value: targetMemberDisplay || t('Unknown')
                })}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              size="small"
              aria-label={t('Close modal')}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            {/* Individual Data (read-only) from the saved Risk Factor record */}
            {individualDataRows.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('Individual Data (from Risk Factor record)')}
                </Typography>
                <Box
                  sx={{
                    border: '0.5px solid',
                    borderColor: 'divider',
                    borderRadius: '8px',
                    p: 1.5
                  }}
                >
                  {individualDataRows.map(([label, value]) => (
                    <Box
                      key={label}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: 0.25
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary'
                        }}
                      >
                        {label}
                      </Typography>
                      <Typography variant="body2">{String(value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Ashkenazi */}
            <Typography variant="subtitle2" gutterBottom>
              {t('Ashkenazi heritage')}
            </Typography>
            <RadioGroup
              row
              value={cur.ashkenazi ?? 'u'}
              onChange={(e) => updateCurrent({ ashkenazi: e.target.value })}
            >
              <FormControlLabel
                value="u"
                control={<Radio />}
                label={t('Unknown')}
              />
              <FormControlLabel
                value="1"
                control={<Radio />}
                label={t('Yes')}
              />
              <FormControlLabel value="0" control={<Radio />} label={t('No')} />
            </RadioGroup>

            {/* Ages */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Cancer diagnosis ages')}
              </Typography>
              <Grid container spacing={2}>
                {cancerSites.map((site) => (
                  <Grid key={site.key} size={6}>
                    <TextField
                      label={t(site.label)}
                      size="small"
                      fullWidth
                      value={cur.ages?.[site.key] ?? ''}
                      onChange={(e) =>
                        updateCurrent({
                          ages: { ...cur.ages, [site.key]: e.target.value }
                        })
                      }
                    />
                  </Grid>
                ))}
              </Grid>
              <CanRiskCancerEvidence evidence={cur.cancerEvidence} />
            </Box>

            {/* Receptors */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Receptor status')}
              </Typography>
              {renderReceptorTable()}
            </Box>

            {/* Genotypes */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Genetic test results')}
              </Typography>
              {renderGenotypeTable()}
            </Box>
          </Box>

          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderTop: 1,
              borderColor: 'divider'
            }}
          >
            <Box>
              <Button
                disabled={pageIdx === 0}
                onClick={() => setPageIdx((i) => i - 1)}
              >
                ←
              </Button>
              <Typography variant="caption" component="span" sx={{ mx: 1 }}>
                {pageIdx + 1} / {pages.length}
              </Typography>
              <Button
                disabled={pageIdx === pages.length - 1}
                onClick={() => setPageIdx((i) => i + 1)}
              >
                →
              </Button>
              <Button
                onClick={() => {
                  const idx = pages.findIndex((p) => {
                    const m = (p.display || '').match(/\((\d+)\)$/);
                    return m && m[1] === jumpUPN;
                  });
                  if (idx !== -1) setPageIdx(idx);
                }}
                disabled={!jumpUPN}
                sx={{ ml: 1 }}
              >
                {t('Go to')}
              </Button>
              <TextField
                size="small"
                placeholder={t('NO')}
                value={jumpUPN}
                onChange={(e) => setJumpUPN(e.target.value)}
                sx={{ width: 60 }}
              />
            </Box>

            <Box>
              <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>
                {t('Cancel')}
              </Button>
              <Button
                onClick={handleDownload}
                variant="contained"
                color="secondary"
                sx={{ mr: 1 }}
              >
                {t('Download')}
              </Button>
              <Button
                onClick={handleCalculate}
                variant="contained"
                color="secondary"
                disabled={!canRiskConfigured || canRiskChecking || calculating}
              >
                {calculating ? t('Waiting…') : t('Get Score')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal open={Boolean(scoreResult)} onClose={() => setScoreResult(null)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 760,
            maxHeight: 720,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {t('CanRisk Score Results')}
            </Typography>
            <IconButton
              onClick={() => setScoreResult(null)}
              size="small"
              aria-label={t('Close modal')}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            <Typography variant="body2" gutterBottom>
              {t('Version:')} {scoreResult?.version || '-'}
            </Typography>
            <Typography variant="body2" gutterBottom>
              {t('Calculated at:')} {scoreResult?.timestamp || '-'}
            </Typography>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('Risk summary')}
              </Typography>
              <Typography variant="body2">
                {t('10-year breast cancer risk:')}{' '}
                {tenYearRisk?.['breast cancer risk']?.percent ?? '-'}%
              </Typography>
              <Typography variant="body2">
                {t('Lifetime breast cancer risk:')}{' '}
                {lifetimeRisk?.['breast cancer risk']?.percent ?? '-'}%
              </Typography>
            </Box>

            {Array.isArray(scoreResult?.warnings) &&
              scoreResult.warnings.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('Warnings')}
                  </Typography>
                  {scoreResult.warnings.map((warning, idx) => (
                    <Typography variant="body2" key={`${warning}-${idx}`}>
                      - {warning}
                    </Typography>
                  ))}
                </Box>
              )}

            {mutationProbabilities.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t('Mutation probabilities')}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('Gene')}</TableCell>
                      <TableCell align="right">{t('Percent')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mutationProbabilities.map((entry, idx) => {
                      const [gene, value] =
                        Object.entries(entry || {})[0] || [];
                      return (
                        <TableRow key={`${gene || 'row'}-${idx}`}>
                          <TableCell>{gene || '-'}</TableCell>
                          <TableCell align="right">
                            {value?.percent ?? '-'}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 1,
                maxHeight: 260,
                overflowY: 'auto',
                bgcolor:
                  theme.appearance.value === 'light'
                    ? '#f5f5f5'
                    : 'rgba(255,255,255,0.08)'
              }}
            >
              <pre
                style={{
                  margin: 0,
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {JSON.stringify(scoreResult, null, 2)}
              </pre>
            </Box>
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Button onClick={() => setScoreResult(null)} variant="contained">
              {t('Close')}
            </Button>
          </Box>
        </Box>
      </Modal>
      <Modal open={Boolean(scoreError)} onClose={() => setScoreError('')}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 760,
            maxHeight: 520,
            bgcolor:
              theme.appearance.value === 'light'
                ? 'white'
                : theme.appearance.shade,
            borderRadius: 2,
            boxShadow: 24,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              backgroundColor: 'secondary.main',
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {t('CanRisk Calculation Error')}
            </Typography>
            <IconButton
              onClick={() => setScoreError('')}
              size="small"
              aria-label={t('Close modal')}
              sx={{ color: '#fff' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {scoreError}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end'
            }}
          >
            <Button onClick={() => setScoreError('')} variant="contained">
              {t('Close')}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
